/* Shared Finance CSV export/import — port từ AppStatic.tsx (giữ nguyên format & round-trip). */
import { api } from "@/lib/api";
import type { Customer, FinanceEntry } from "../../AppStatic";
import { getErrorMessage } from "../../AppStatic";
import { CSV_BOM, csvEscape, parseCsv } from "./csvCore";

export const FINANCE_CSV_COLUMNS = [
  "id", "occurred_on", "kind", "category", "item_name", "quantity", "unit_price",
  "amount", "currency", "note", "auto", "revenue_stream",
  "customer_id", "task_id", "project_id",
  "payer_name", "payment_method", "match_status",
  "created_by", "created_at", "updated_at",
] as const;

export function exportFinanceCsv(
  rows: FinanceEntry[],
  customerMap?: Map<string, Customer>,
  taskTitles?: Map<string, string>,
): string {
  // Lossless export — snake_case header, raw values; round-trip an toàn qua importFinanceCsv.
  // Thêm 2 cột "đọc cho người" customer_name + task_title (KHÔNG dùng khi import).
  const header = [...FINANCE_CSV_COLUMNS, "customer_name", "customer_real_name", "customer_bank_transfer_names", "task_title"] as const;
  const lines = [header.map(csvEscape).join(",")];
  for (const r of rows) {
    const row = header.map((k) => {
      if (k === "customer_name") return r.customer_id ? (customerMap?.get(r.customer_id)?.name ?? "") : "";
      if (k === "customer_real_name") return r.customer_id ? ((customerMap?.get(r.customer_id) as any)?.real_name ?? "") : "";
      if (k === "customer_bank_transfer_names") {
        const btn = r.customer_id ? ((customerMap?.get(r.customer_id) as any)?.bank_transfer_names ?? []) : [];
        return Array.isArray(btn) ? btn.join(";") : "";
      }
      if (k === "task_title") {
        const t = r.task_id ? (taskTitles?.get(r.task_id) ?? "") : "";
        if (t) return t;
        // Fallback: dùng item_name nhưng bỏ prefix "Cọc — "
        return r.item_name ? r.item_name.replace(/^Cọc\s*[—-]\s*/, "") : "";
      }
      const v = (r as Record<string, unknown>)[k];
      if (v == null) return "";
      if (typeof v === "boolean") return v ? "1" : "0";
      return String(v);
    });
    lines.push(row.map(csvEscape).join(","));
  }
  return CSV_BOM + lines.join("\r\n");
}

export async function importFinanceCsv(file: File): Promise<{ created: number; updated: number; skipped: number; errors: string[] }> {
  const text = (await file.text()).replace(/^\ufeff/, "");
  const rows = parseCsv(text);
  const result = { created: 0, updated: 0, skipped: 0, errors: [] as string[] };
  if (rows.length < 2) return result;
  const headers = rows[0].map((h) => h.trim());
  const has = (k: string) => headers.includes(k);
  if (!has("kind") || !has("amount")) {
    throw new Error("File CSV thiếu cột bắt buộc: kind, amount");
  }
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const get = (k: string) => {
      const i = headers.indexOf(k);
      return i >= 0 ? (row[i] ?? "") : "";
    };
    const body: Record<string, unknown> = {};
    for (const k of headers) {
      // chỉ gửi field xuất hiện trong CSV — KHÔNG ghi đè field thiếu thành null.
      const raw = get(k);
      if (k === "id" || k === "created_at" || k === "updated_at") continue;
      // Cột "đọc cho người" — không gửi lên backend khi import.
      if (k === "customer_name" || k === "task_title" || k === "customer_real_name" || k === "customer_bank_transfer_names") continue;
      if (k === "amount" || k === "quantity" || k === "unit_price") {
        if (raw === "") { body[k] = null; continue; }
        const n = Number(raw);
        if (!Number.isFinite(n)) continue;
        body[k] = n;
        continue;
      }
      if (k === "auto") {
        const s = raw.trim().toLowerCase();
        body[k] = s === "1" || s === "true" || s === "yes";
        continue;
      }
      body[k] = raw === "" ? null : raw;
    }
    if (body.kind == null || body.amount == null) { result.skipped++; continue; }
    const id = get("id").trim();
    try {
      if (id) {
        await api.put(`/finance/${encodeURIComponent(id)}`, body);
        result.updated++;
      } else {
        await api.post("/finance", body);
        result.created++;
      }
    } catch (e) {
      result.errors.push(`Dòng ${r + 1}: ${getErrorMessage(e, "Lỗi không rõ")}`);
    }
  }
  return result;
}
